import React, { useEffect, useState } from "react";
import {
  Autocomplete,
  Box,
  Divider,
  Skeleton,
  Stack,
  Switch,
  TextField,
  Typography,
} from "@mui/material";
import { BooleanField } from "../ConfigFields/BooleanField";
import { StringField } from "../ConfigFields/StringField";
import { sectionLabelSx } from "../../utils";
import { ColorsConstants } from "../../../../cssUtils/constants";
import typography from "../../../../cssUtils/typography";
import { useAuth } from "../../../../hooks/AuthProvider";
import {
  NODEBOT_FALLBACK_AUTORESPONDERS,
  TEMPLATE_ENDPOINT,
  USERS_ENDPOINT,
} from "../../../../config/urls";
import {
  EndChatFields,
  NotificationTemplateSelect,
  NotificationUsersSelect,
  QueueCleanupFields,
  liveAgentSwitchSx,
} from "../../../../common/live-agent-settings-fields";

// Same options and labels as the Live Agent autoresponder settings
// (FallbackAutoresponderEdit.js).
const TRIGGER_TYPE_OPTIONS = [
  { value: "IMMEDIATE", label: "Immediate" },
  { value: "AUTO_RESPONSE", label: "Auto Response" },
];

const DEFAULT_END_CHAT_MESSAGE =
  "Thank you for contacting us! Hope we were able to help. Have a great day \u{1F60A}";

const OVERRIDE_SWITCHES = [
  "override_handoff",
  "override_notifications",
  "override_end_chat",
  "override_queue_cleanup",
];

// The values each section starts with when its override switch is on, taken from the
// tenant's active Live Agent Fallback Autoresponder (or the autoresponder screen's
// fixed defaults when there is none). A one-time copy: it doesn't follow later
// edits to the autoresponder.
const sectionValues = (autoresponder) => ({
  override_handoff: {
    trigger_type: autoresponder?.live_agent_trigger_type || "IMMEDIATE",
    ...(autoresponder?.live_agent_trigger_type === "AUTO_RESPONSE" &&
    autoresponder.live_agent_initial_message
      ? { initial_message: autoresponder.live_agent_initial_message }
      : {}),
  },
  override_notifications: {
    email_notification_users: autoresponder?.email_notification_users || [],
    whatsapp_notification_users:
      autoresponder?.whatsapp_notification_users || [],
    ...(autoresponder?.whatsapp_template
      ? { whatsapp_template: autoresponder.whatsapp_template }
      : {}),
  },
  override_end_chat: {
    end_chat_message_mode:
      autoresponder?.end_chat_message_mode || "SEND_MESSAGE",
    end_chat_message:
      autoresponder?.end_chat_message || DEFAULT_END_CHAT_MESSAGE,
  },
  override_queue_cleanup: {
    unassigned_queue_cleanup_hours:
      autoresponder?.unassigned_queue_cleanup_hours || 12,
  },
});

// Which sections start switched on for a node that has never been saved with any
// override switch. Trigger type and end chat are always set on an autoresponder;
// notifications and queue cleanup are opt-in there, so they start on only if the
// autoresponder has them set.
const sectionsStartingOn = (autoresponder) => ({
  override_handoff: !!autoresponder,
  override_notifications:
    (autoresponder?.email_notification_users || []).length > 0 ||
    (autoresponder?.whatsapp_notification_users || []).length > 0,
  override_end_chat: !!autoresponder,
  override_queue_cleanup: !!autoresponder?.unassigned_queue_cleanup_enabled,
});

const isBlank = (value) => !value || !String(value).trim();

// Field errors for the node's config, mirroring the autoresponder screen's rules.
// Only sections whose override switch is on are checked: the rest use the
// autoresponder's settings and ignore whatever is saved on the node.
export const validateLiveAgentConfig = (config) => {
  const errs = {};

  // While the node is disabled its override sections are hidden and unused, so
  // nothing in them can block a save.
  if (config.enabled === false) return errs;

  if (
    config.override_handoff &&
    config.trigger_type === "AUTO_RESPONSE" &&
    isBlank(config.initial_message)
  ) {
    errs.initial_message = "Initial message is required for Auto Response";
  }

  if (config.override_notifications) {
    const hasWhatsappUsers =
      (config.whatsapp_notification_users || []).length > 0;
    if (hasWhatsappUsers && !config.whatsapp_template) {
      errs.whatsapp_template = "Select a WhatsApp template for notifications";
    }
    if (config.whatsapp_template && !hasWhatsappUsers) {
      errs.whatsapp_notification_users =
        "Select at least one WhatsApp user for notifications";
    }
  }

  if (
    config.override_end_chat &&
    config.end_chat_message_mode === "SEND_MESSAGE" &&
    isBlank(config.end_chat_message)
  ) {
    errs.end_chat_message = "End chat message is required";
  }

  if (config.override_queue_cleanup) {
    const hours = Number(config.unassigned_queue_cleanup_hours);
    if (
      config.unassigned_queue_cleanup_hours === undefined ||
      !Number.isInteger(hours) ||
      hours < 1 ||
      hours > 168
    ) {
      errs.unassigned_queue_cleanup_hours =
        "Enter a whole number of hours between 1 and 168";
    }
  }

  return errs;
};

// A settings section with its own override switch. Off: the node uses the Fallback
// Autoresponder's settings for it (values saved on the node are kept but ignored).
// On: the fields are shown and replace the autoresponder's.
const OverrideSection = ({ title, description, checked, onToggle, children }) => (
  <Box>
    <Divider sx={{ mb: 3 }} />
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        mb: 1,
      }}
    >
      <Box>
        <Typography sx={sectionLabelSx}>{title}</Typography>
        <Typography
          sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray60 }}
        >
          {checked
            ? description
            : "Using the Fallback Autoresponder's settings."}
        </Typography>
      </Box>
      <Switch
        checked={checked}
        onChange={(e) => onToggle(e.target.checked)}
        inputProps={{ "aria-label": `Override ${title}` }}
        sx={liveAgentSwitchSx}
      />
    </Box>
    {checked && <Stack spacing={3} sx={{ mt: 2 }}>{children}</Stack>}
  </Box>
);

// Stand-in for the panel while it loads, laid out like the real thing (intro text,
// the two top fields, then each section's header with the body most likely to be
// open) so the content replacing it moves as little as possible.
const SectionSkeleton = ({ bodyHeights = [] }) => (
  <Box>
    <Divider sx={{ mb: 3 }} />
    <Box
      sx={{
        display: "flex",
        alignItems: "flex-start",
        justifyContent: "space-between",
        mb: 1,
      }}
    >
      <Box sx={{ width: "70%" }}>
        <Skeleton variant="text" width="55%" height={22} />
        <Skeleton variant="text" width="90%" height={18} />
      </Box>
      <Skeleton variant="rounded" width={58} height={38} />
    </Box>
    {bodyHeights.length > 0 && (
      <Stack spacing={3} sx={{ mt: 2 }}>
        {bodyHeights.map((height, index) => (
          <Skeleton key={index} variant="rounded" height={height} />
        ))}
      </Stack>
    )}
  </Box>
);

const LiveAgentSkeleton = () => (
  <Stack
    spacing={3}
    role="status"
    aria-busy="true"
    aria-label="Loading Live Agent settings"
  >
    <Box>
      <Skeleton variant="text" height={18} />
      <Skeleton variant="text" height={18} />
      <Skeleton variant="text" width="60%" height={18} />
    </Box>
    <Skeleton variant="rounded" height={40} />
    <Skeleton variant="rounded" height={56} />
    <SectionSkeleton bodyHeights={[40, 56]} />
    <SectionSkeleton />
    <SectionSkeleton bodyHeights={[96, 96]} />
    <SectionSkeleton />
  </Stack>
);

// What the panel needs from the server: the users and templates to pick from, and
// the active Live Agent autoresponder the sections start from. Kept briefly in
// memory so reopening a node (or opening another one) doesn't refetch and flash a
// spinner every time.
const LOOKUP_TTL_MS = 60 * 1000;
let lookupCache = null;

const cachedLookups = () => {
  const tenant = localStorage.getItem("tenant");
  return lookupCache &&
    lookupCache.tenant === tenant &&
    Date.now() - lookupCache.at < LOOKUP_TTL_MS
    ? lookupCache.data
    : null;
};

const EMPTY_LOOKUPS = {
  users: [],
  templates: [],
  autoresponder: null,
  autoresponderLoaded: false,
};

const fetchLookups = async (getValidToken) => {
  const headers = {
    Authorization: `Bearer ${await getValidToken()}`,
    "Content-Type": "application/json",
    "X-Tenant-Id": localStorage.getItem("tenant"),
  };

  const [usersResponse, templatesResponse, autoresponderResponse] =
    await Promise.all([
      fetch(USERS_ENDPOINT, { headers }),
      fetch(
        `${TEMPLATE_ENDPOINT}?category_filter=utility&limit=1000&ordering=-last_edited`,
        { headers },
      ),
      fetch(`${NODEBOT_FALLBACK_AUTORESPONDERS}?limit=200`, { headers }),
    ]);

  const data = { ...EMPTY_LOOKUPS };
  if (usersResponse.ok) {
    data.users = (await usersResponse.json()).results || [];
  }
  if (templatesResponse.ok) {
    data.templates = ((await templatesResponse.json()).results || []).filter(
      (t) => t.active && t.status === "APPROVED",
    );
  }
  if (autoresponderResponse.ok) {
    data.autoresponder =
      ((await autoresponderResponse.json()).results || []).find(
        (r) => r.is_active && r.type === "live_agent",
      ) || null;
    data.autoresponderLoaded = true;
    // Only a complete answer is worth keeping.
    lookupCache = {
      tenant: localStorage.getItem("tenant"),
      at: Date.now(),
      data,
    };
  }
  return data;
};

const LiveAgentPreset = ({ schema, config, handleConfigChange }) => {
  const { getValidToken } = useAuth();
  const properties = schema?.properties || {};
  const errors = validateLiveAgentConfig(config);

  // null until loaded. "autoresponderLoaded" tells "no active autoresponder" apart
  // from "couldn't fetch it".
  const [lookups, setLookups] = useState(cachedLookups);

  useEffect(() => {
    if (lookups) return;
    fetchLookups(getValidToken)
      .then(setLookups)
      .catch((err) => {
        console.error("Failed to load live agent options:", err);
        setLookups(EMPTY_LOOKUPS);
      });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const { users, templates, autoresponder, autoresponderLoaded } =
    lookups || EMPTY_LOOKUPS;

  // A node never saved with any override switch (e.g. a seeded one) starts from the
  // autoresponder: each section's switch is on when the autoresponder has that
  // setting, with its values copied in. Only local state until the user saves, and
  // it never runs again once any switch is saved on the node.
  const initialised = OVERRIDE_SWITCHES.some((key) => config[key] !== undefined);
  useEffect(() => {
    if (!autoresponderLoaded || initialised) return;
    const startingOn = sectionsStartingOn(autoresponder);
    const values = sectionValues(autoresponder);
    OVERRIDE_SWITCHES.forEach((section) => {
      handleConfigChange(section, startingOn[section]);
      if (startingOn[section]) {
        Object.entries(values[section]).forEach(([key, value]) =>
          handleConfigChange(key, value),
        );
      }
    });
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [autoresponderLoaded, initialised]);

  // Turning a section on starts it from the autoresponder's values, but never
  // overwrites values already saved on the node from an earlier toggle.
  const toggleSection = (section) => (on) => {
    handleConfigChange(section, on);
    if (on) {
      Object.entries(sectionValues(autoresponder)[section]).forEach(
        ([key, value]) => {
          if (config[key] === undefined) handleConfigChange(key, value);
        },
      );
    }
  };

  const setUserIds = (key, selected) =>
    handleConfigChange(
      key,
      selected.map((u) => u.id),
    );

  const usersFor = (key) =>
    users.filter((u) => (config[key] || []).includes(u.id));

  const whatsappUsers = usersFor("whatsapp_notification_users");
  const enabled = config["enabled"] ?? true;

  // Nothing is shown until the lookups are in and the starting state has been
  // applied, so the panel appears once in its final form instead of shifting.
  const ready = !!lookups && (initialised || !autoresponderLoaded);
  if (!ready) return <LiveAgentSkeleton />;

  return (
    <Stack spacing={3}>
      <Typography
        variant="bodySmall"
        sx={{ color: ColorsConstants.grayFontColor }}
      >
        Hands the conversation to a live agent using the Fallback Autoresponder's
        Live Agent settings. Switch on a section below to override it for this
        node. Connect the "Enabled" path to what should follow, and the other
        path to a fallback message.
      </Typography>

      {autoresponderLoaded && !autoresponder && (
        <Typography sx={{ ...typography.bodySmall, color: "#b50000" }}>
          No Live Agent autoresponder is active, so live agent hand-off won't work
          until one is activated.
        </Typography>
      )}

      <BooleanField
        fieldKey="enabled"
        schema={properties.enabled || {}}
        value={config["enabled"] ?? true}
        required={true}
        onChange={(val) => handleConfigChange("enabled", val)}
      />

      <StringField
        fieldKey="unavailable_message"
        schema={{
          ...(properties.unavailable_message || {}),
          // StringField uses the description as the placeholder; the schema's is long
          // enough to overflow the textarea and add a scrollbar while it is empty.
          description: "Leave blank to use the default message",
        }}
        value={config["unavailable_message"]}
        required={false}
        onChange={(val) => handleConfigChange("unavailable_message", val)}
      />

      {enabled ? (
        <>
          <OverrideSection
            title="Hand-off"
            description="Replaces the autoresponder's trigger type and initial message."
            checked={!!config.override_handoff}
            onToggle={toggleSection("override_handoff")}
          >
            <Autocomplete
              disableClearable
              options={TRIGGER_TYPE_OPTIONS}
              value={
                TRIGGER_TYPE_OPTIONS.find(
                  (o) => o.value === (config["trigger_type"] || "IMMEDIATE"),
                ) || TRIGGER_TYPE_OPTIONS[0]
              }
              getOptionLabel={(option) => option.label}
              isOptionEqualToValue={(option, val) => option.value === val.value}
              onChange={(event, newValue) => {
                handleConfigChange("trigger_type", newValue.value);
                // Only Auto Response has an initial message (as on the autoresponder
                // screen), so drop a leftover one when switching away from it.
                if (newValue.value !== "AUTO_RESPONSE") {
                  handleConfigChange("initial_message", undefined);
                }
              }}
              renderInput={(params) => (
                <TextField
                  {...params}
                  label="Trigger Type"
                  size="small"
                  fullWidth
                />
              )}
              sx={{ "& .MuiOutlinedInput-root": { fontSize: 14 } }}
            />

            {config["trigger_type"] === "AUTO_RESPONSE" && (
              <Box>
                <StringField
                  fieldKey="initial_message"
                  schema={properties.initial_message || {}}
                  value={config["initial_message"]}
                  required={true}
                  onChange={(val) => handleConfigChange("initial_message", val)}
                />
                {errors.initial_message && (
                  <Typography
                    sx={{ ...typography.bodySmall, color: "#b50000", mt: 0.5 }}
                  >
                    {errors.initial_message}
                  </Typography>
                )}
              </Box>
            )}
          </OverrideSection>

          <OverrideSection
            title="Agent Connection Notifications"
            description="Replaces who is notified when a customer connects. An empty list notifies nobody."
            checked={!!config.override_notifications}
            onToggle={toggleSection("override_notifications")}
          >
            <Box>
              <Typography sx={{ ...typography.bodySmall, mb: 1 }}>
                Notify via Email
              </Typography>
              <NotificationUsersSelect
                kind="email"
                users={users}
                value={usersFor("email_notification_users")}
                onChange={(selected) =>
                  setUserIds("email_notification_users", selected)
                }
              />
            </Box>

            <Box>
              <Typography sx={{ ...typography.bodySmall, mb: 1 }}>
                Notify via WhatsApp
              </Typography>
              <NotificationUsersSelect
                kind="whatsapp"
                users={users}
                value={whatsappUsers}
                onChange={(selected) =>
                  setUserIds("whatsapp_notification_users", selected)
                }
                error={errors.whatsapp_notification_users}
              />

              {(whatsappUsers.length > 0 || config["whatsapp_template"]) && (
                <Box sx={{ mt: 2 }}>
                  <NotificationTemplateSelect
                    templates={templates}
                    value={
                      templates.find(
                        (t) => t.template_id === config["whatsapp_template"],
                      ) || null
                    }
                    onChange={(template) =>
                      handleConfigChange(
                        "whatsapp_template",
                        template?.template_id ?? undefined,
                      )
                    }
                    error={errors.whatsapp_template}
                  />
                </Box>
              )}
            </Box>
          </OverrideSection>

          <OverrideSection
            title="On end chat by support agent"
            description="Replaces what the customer is sent when the chat ends."
            checked={!!config.override_end_chat}
            onToggle={toggleSection("override_end_chat")}
          >
            <Box>
              <EndChatFields
                compact
                mode={config["end_chat_message_mode"] || "SEND_MESSAGE"}
                message={config["end_chat_message"] || ""}
                onModeChange={(mode) =>
                  handleConfigChange("end_chat_message_mode", mode)
                }
                onMessageChange={(value) =>
                  handleConfigChange("end_chat_message", value)
                }
                error={errors.end_chat_message}
              />
            </Box>
          </OverrideSection>

          <OverrideSection
            title="Unassigned queue cleanup"
            description="Auto-closes unclaimed conversations with unread messages after the hours below, replacing the autoresponder's setting."
            checked={!!config.override_queue_cleanup}
            onToggle={toggleSection("override_queue_cleanup")}
          >
            <Box>
              <QueueCleanupFields
                compact
                alwaysEnabled
                hours={config["unassigned_queue_cleanup_hours"] ?? ""}
                onHoursChange={(hours) =>
                  handleConfigChange(
                    "unassigned_queue_cleanup_hours",
                    hours === "" ? undefined : hours,
                  )
                }
                error={errors.unassigned_queue_cleanup_hours}
              />
            </Box>
          </OverrideSection>
        </>
      ) : (
        <Typography
          sx={{ ...typography.bodySmall, color: ColorsConstants.primaryGray60 }}
        >
          Live agent hand-off is off for this node, so the override sections are
          hidden and not used. Turn Enabled on to configure them.
        </Typography>
      )}
    </Stack>
  );
};

export default LiveAgentPreset;
